import { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BotMessageSquare, MessageSquare } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { PainelPedidoContext } from '@/contexts/PainelPedidoContext';
import {
  CATEGORIAS_VENDA_IA, categoriasDasVendas, estadoDaVendaIa, inicioDoMes, resumirPeriodo, vendasDoMes,
  type EstadoVendaIa, type VendaIa,
} from '@/lib/vendasIa';
import { useVendasIaKpi, type SegmentoVendasIa } from '@/hooks/useVendasIaKpi';

/**
 * O controle das vendas da IsabellA (Douglas 26/09/2026): "troque isso pelas
 * vendas IA" — no lugar do chip de transferências da IA, que não existe mais.
 *
 * Responde três perguntas, nesta ordem:
 *  1. quanto ela vendeu no mês, por categoria (Altas · Fibra · TT · Renovação);
 *  2. quanto disso virou pedido;
 *  3. o que está ESPERANDO alguém lançar o pedido, e de qual consultor.
 *
 * Tudo sai da MESMA lista (kpi_vendas_ia): os quadros de categoria, os contadores
 * dos filtros, a tabela e o total são reduce do mesmo array.
 */

const GRID = 'grid grid-cols-[82px_minmax(0,1.7fr)_minmax(0,1fr)_minmax(0,0.9fr)_84px_minmax(0,1.5fr)_36px] gap-2 px-3';
const CARD = 'rounded-xl border border-border bg-card shadow-[0_1px_2px_rgba(60,20,30,0.05),0_4px_14px_-6px_rgba(60,20,30,0.08)]';
const VERDE = 'text-[#075E54] dark:text-[#25D366]';
const BORDO = 'text-[#7A2235] dark:text-[#C87484]';

type Filtro = 'esperando' | 'pedido' | 'nao-era' | 'todas';

const IAS: Array<{ segmento: SegmentoVendasIa; nome: string; rotulo: string; titulo: string }> = [
  { segmento: 'PJ', nome: 'IsabellA', rotulo: 'IsabellA · CNPJ', titulo: 'Prime · CNPJ' },
  { segmento: 'PF', nome: 'IA Douglas', rotulo: 'Douglas · CPF', titulo: 'Conecta · CPF' },
];

const ROTULO_CATEGORIA: Record<string, string> = {
  ...Object.fromEntries(CATEGORIAS_VENDA_IA.map((c) => [c.chave, c.rotulo])),
  APARELHO: 'Aparelho',
  SERVICO: 'Serviço',
  NAO_INFORMADO: 'Tipo não informado',
};

function dotDoEstado(estado: EstadoVendaIa): string {
  switch (estado) {
    case 'PEDIDO_ATIVO': return 'bg-[#075E54] dark:bg-[#25D366] shadow-[0_0_0_3px_rgba(7,94,84,0.16)]';
    case 'PEDIDO_ANDAMENTO': return 'border-[1.5px] border-[#075E54] dark:border-[#25D366]';
    case 'PEDIDO_CANCELADO': return 'bg-[#E88A8A]';
    case 'ESPERANDO': return 'bg-[#7A2235] dark:bg-[#C87484]';
    default: return 'bg-muted-foreground/25';
  }
}

function tempoDesde(iso: string): string {
  const horas = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000));
  if (horas < 1) return 'agora';
  if (horas < 24) return `há ${horas} h`;
  const dias = Math.floor(horas / 24);
  return `há ${dias} ${dias === 1 ? 'dia' : 'dias'}`;
}

function pedidosDaVenda(v: VendaIa): string[] {
  return [...new Set(v.linhas.filter((l) => l.situacao === 'VIROU_PEDIDO' && l.pedido).map((l) => String(l.pedido)))];
}

/** O que vem depois do número do pedido: a etapa em que ele está agora. */
function complementoDoPedido(v: VendaIa, estado: EstadoVendaIa): string {
  if (estado === 'PEDIDO_ATIVO') return 'ativo';
  if (estado === 'PEDIDO_CANCELADO') return 'cancelado';
  return v.linhas.find((l) => l.situacao === 'VIROU_PEDIDO' && l.coluna)?.coluna ?? '';
}

function textoDoEstado(v: VendaIa, estado: EstadoVendaIa): string {
  const pedidos = pedidosDaVenda(v);
  if (pedidos.length) {
    const complemento = complementoDoPedido(v, estado);
    return `Pedido ${pedidos.join(', ')}${complemento ? ` · ${complemento}` : ''}`;
  }
  if (estado === 'ESPERANDO') return `Esperando pedido · ${tempoDesde(v.registrada_em)}`;
  return v.anulada ? 'Não era dela · anulada' : 'Não era dela · pedido do consultor';
}

function fmtBRL(v: number | null | undefined): string {
  if (v == null || v <= 0) return '—';
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function fmtQuando(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  }).replace(',', '');
}

function fmtCnpj(cnpj: string | null): string {
  const d = String(cnpj ?? '').replace(/\D/g, '');
  if (d.length === 11) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  return d.length === 14 ? d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5') : (cnpj ?? '');
}

function nomeDoMes(diaIso: string): string {
  const nome = new Date(`${diaIso}T12:00:00Z`).toLocaleDateString('pt-BR', { month: 'long', timeZone: 'UTC' });
  return nome.charAt(0).toUpperCase() + nome.slice(1);
}

interface Props {
  aberto: boolean;
  onFechar: (aberto: boolean) => void;
  hoje: string;
  vendas: VendaIa[];
}

export function ModalVendasIa({ aberto, onFechar, hoje, vendas: vendasPJ }: Props) {
  const navigate = useNavigate();
  const [segmento, setSegmento] = useState<SegmentoVendasIa>('PJ');
  const ia = IAS.find((i) => i.segmento === segmento) ?? IAS[0];
  const { data: dadosPF, isPending: carregandoPF, isError: erroPF } = useVendasIaKpi(aberto && segmento === 'PF', 'PF');
  const vendas = useMemo(
    () => (segmento === 'PF' ? dadosPF?.vendas ?? [] : vendasPJ),
    [segmento, dadosPF, vendasPJ],
  );
  const painelPedido = useContext(PainelPedidoContext);
  const [mesesAtras, setMesesAtras] = useState(0);
  const [filtro, setFiltro] = useState<Filtro>('esperando');

  const doMes = useMemo(() => vendasDoMes(vendas, hoje, mesesAtras), [vendas, hoje, mesesAtras]);
  const comEstado = useMemo(() => doMes.map((v) => ({ v, estado: estadoDaVendaIa(v) })), [doMes]);
  const resumo = useMemo(() => resumirPeriodo(doMes), [doMes]);
  const categorias = useMemo(() => categoriasDasVendas(doMes), [doMes]);

  const lista = useMemo(() => comEstado.filter(({ estado }) => {
    if (filtro === 'esperando') return estado === 'ESPERANDO';
    if (filtro === 'pedido') return estado === 'PEDIDO_ATIVO' || estado === 'PEDIDO_ANDAMENTO' || estado === 'PEDIDO_CANCELADO';
    if (filtro === 'nao-era') return estado === 'NAO_ERA_DELA';
    return true;
  }), [comEstado, filtro]);
  const valorDaLista = useMemo(() => lista.reduce((s, { v }) => s + (Number(v.valor) || 0), 0), [lista]);

  const FILTROS: Array<{ id: Filtro; rotulo: string; total: number }> = [
    { id: 'esperando', rotulo: 'Esperando pedido', total: resumo.esperando },
    { id: 'pedido', rotulo: 'Viraram pedido', total: resumo.pedido },
    { id: 'nao-era', rotulo: 'Não eram dela', total: resumo.naoEraDela },
    { id: 'todas', rotulo: 'Todas', total: doMes.length },
  ];
  const meses = [0, 1].map((n) => ({ n, rotulo: nomeDoMes(inicioDoMes(hoje, n)) }));

  /* O pedido abre no Painel do Pedido (o mesmo do Kanban e da Planilha), pelo
     número: o painel resolve o pedido-mãe. O controle fecha antes, como no
     atalho da conversa — o painel por cima de um modal aberto fechava os dois. */
  const abrirPedido = (numero: string) => {
    onFechar(false);
    painelPedido.open(`P${numero}`, 'visao360');
  };

  const abrirConversa = (conversaId: string | null) => {
    if (!conversaId) return;
    onFechar(false);
    navigate(`/whatsapp-inbox?conversation=${conversaId}`);
  };

  return (
    <Dialog open={aberto} onOpenChange={onFechar}>
      <DialogContent className="max-w-6xl h-[82vh] p-0 flex flex-col gap-0">
        <DialogHeader className="px-4 pt-4 pb-3 flex-none space-y-3">
          <div className="flex items-center gap-2 flex-nowrap pr-8">
            <DialogTitle className="flex items-center gap-2 text-base">
              <BotMessageSquare className="h-4 w-4 text-primary" />
              Vendas da {ia.nome}
              <span className="text-[11px] font-normal text-muted-foreground">{ia.titulo}</span>
            </DialogTitle>
            <div className="ml-auto flex items-center gap-0.5 rounded-lg bg-[#EDE8E0] dark:bg-[#161010] p-0.5 shrink-0">
              {IAS.map((i) => (
                <button
                  key={i.segmento}
                  type="button"
                  onClick={() => { setSegmento(i.segmento); setFiltro('esperando'); }}
                  className={cn(
                    'h-7 rounded-md px-2.5 text-[11px] font-medium whitespace-nowrap transition-colors',
                    segmento === i.segmento ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {i.rotulo}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-0.5 rounded-lg bg-[#EDE8E0] dark:bg-[#161010] p-0.5 shrink-0">
              {meses.map((m) => (
                <button
                  key={m.n}
                  type="button"
                  onClick={() => setMesesAtras(m.n)}
                  className={cn(
                    'h-7 rounded-md px-2.5 text-[11px] font-medium whitespace-nowrap transition-colors',
                    mesesAtras === m.n ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {m.rotulo}
                </button>
              ))}
            </div>
          </div>

          {/* As quatro réguas em que ela é medida. Categoria conta LINHAS. */}
          <div className="grid grid-cols-5 gap-2">
            <div className={cn(CARD, 'px-3 py-2')}>
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Vendas dela</div>
              <div className="font-display text-[22px] font-bold tabular-nums leading-tight text-primary">{resumo.vendas}</div>
              <div className="text-[10.5px] text-muted-foreground tabular-nums">
                <span className={resumo.pedido > 0 ? VERDE : undefined}>{resumo.pedido} viraram pedido</span>
              </div>
            </div>
            {categorias.map((c) => (
              <div key={c.chave} className={cn(CARD, 'px-3 py-2')}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{c.rotulo}</div>
                <div className="font-display text-[22px] font-bold tabular-nums leading-tight text-primary">{c.linhas}</div>
                <div className="text-[10.5px] text-muted-foreground tabular-nums">
                  <span className={c.pedido > 0 ? VERDE : undefined}>{c.pedido} em pedido</span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-1.5 flex-nowrap">
            {FILTROS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFiltro(f.id)}
                className={cn(
                  'h-8 rounded-md px-2.5 text-[11px] font-medium shrink-0 whitespace-nowrap transition-colors tabular-nums',
                  filtro === f.id ? 'bg-primary text-primary-foreground' : 'border border-border text-muted-foreground hover:text-foreground hover:bg-accent',
                )}
              >
                {f.rotulo} · {f.total}
              </button>
            ))}
          </div>
        </DialogHeader>

        {lista.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground px-6 text-center">
            {segmento === 'PF' && carregandoPF
              ? 'Carregando as vendas do CPF…'
              : segmento === 'PF' && erroPF
              ? 'Não foi possível carregar as vendas do CPF. Feche e abra de novo.'
              : doMes.length === 0
              ? `Nenhuma venda registrada pela ${ia.nome} em ${meses[mesesAtras].rotulo.toLowerCase()}.`
              : 'Nenhuma venda neste filtro. Toque em "Todas" para ver o mês inteiro.'}
          </div>
        ) : (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className={cn(GRID, 'sticky top-0 z-10 bg-[#FAF8F4] dark:bg-[#241A1A] h-7 items-center text-[8.5px] font-bold uppercase tracking-[1.1px] text-muted-foreground border-b border-border')}>
                <span className="truncate">Quando</span>
                <span className="truncate">Cliente</span>
                <span className="truncate">Consultor dono</span>
                <span className="truncate">O que vendeu</span>
                <span className="truncate text-right">Valor/mês</span>
                <span className="truncate">Situação</span>
                <span />
              </div>
              {lista.map(({ v, estado }) => {
                const porCategoria = new Map<string, number>();
                for (const l of v.linhas) {
                  const rotulo = ROTULO_CATEGORIA[l.categoria] ?? l.categoria;
                  porCategoria.set(rotulo, (porCategoria.get(rotulo) ?? 0) + 1);
                }
                const cats = [...porCategoria].map(([rotulo, n]) => `${n} ${rotulo}`);
                const situacao = textoDoEstado(v, estado);
                return (
                  <div
                    key={v.venda_id}
                    className={cn(
                      GRID,
                      'h-11 items-center border-b border-border/50 hover:bg-[#F4F0EA] dark:hover:bg-[#2A1F1F] even:bg-[#FBF9F6] dark:even:bg-[#241A1A]',
                      estado === 'NAO_ERA_DELA' && 'text-muted-foreground',
                    )}
                  >
                    <span className="truncate text-[12px] tabular-nums text-muted-foreground">{fmtQuando(v.registrada_em)}</span>
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-semibold" title={v.empresa ?? undefined}>{v.empresa ?? '—'}</span>
                      <span className="block truncate text-[10.5px] tabular-nums text-muted-foreground">{fmtCnpj(v.cnpj)}</span>
                    </span>
                    <span className="truncate text-[12px]" title={v.consultor ?? undefined}>{v.consultor ?? '—'}</span>
                    <span className="truncate text-[12px]" title={cats.join(' · ')}>{cats.join(' · ')}</span>
                    <span className="truncate text-right text-[12.5px] tabular-nums">{fmtBRL(Number(v.valor))}</span>
                    <span className="flex items-center gap-1.5 min-w-0" title={situacao}>
                      <span className={cn('h-2 w-2 rounded-full shrink-0', dotDoEstado(estado))} />
                      {estado !== 'NAO_ERA_DELA' && pedidosDaVenda(v).length > 0 ? (
                        <span className="truncate text-[12px]">
                          Pedido{' '}
                          {pedidosDaVenda(v).map((numero, i) => (
                            <span key={numero}>
                              {i > 0 && ', '}
                              <button
                                type="button"
                                onClick={() => abrirPedido(numero)}
                                title={`Abrir o pedido ${numero}`}
                                className="font-semibold tabular-nums text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary"
                              >
                                {numero}
                              </button>
                            </span>
                          ))}
                          {complementoDoPedido(v, estado) && <> · {complementoDoPedido(v, estado)}</>}
                        </span>
                      ) : (
                        <span className={cn('truncate text-[12px]', estado === 'ESPERANDO' && cn(BORDO, 'font-semibold'))}>{situacao}</span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => abrirConversa(v.conversa_id)}
                      disabled={!v.conversa_id}
                      title="Abrir a conversa no Inbox"
                      className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-border text-muted-foreground hover:text-primary hover:border-primary disabled:cursor-not-allowed disabled:text-muted-foreground/40 disabled:hover:border-border"
                    >
                      <MessageSquare className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>

            {/* TOTAL fora do scroll, e a régua de leitura escrita uma vez. */}
            <div className={cn(GRID, 'flex-none h-9 items-center bg-[#FAF8F4] dark:bg-[#241A1A] border-t-2 border-border text-[12px] font-bold')}>
              <span
                className="truncate col-span-4 text-[10px] font-normal text-muted-foreground"
                title={`Venda = registro da ${ia.nome} · categoria conta linhas (uma venda pode ter TT e fibra) · virou pedido = pedido da mesma linha lançado depois do registro · não era dela = o pedido do consultor já existia`}
              >
                {lista.length} {lista.length === 1 ? 'venda' : 'vendas'} · Venda = registro da {ia.nome} · categoria conta linhas ·
                {' '}virou pedido = pedido da mesma linha lançado depois do registro · não era dela = o pedido do consultor já existia
              </span>
              <span className="truncate text-right tabular-nums">{fmtBRL(valorDaLista)}</span>
              <span />
              <span />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
